import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import type { ReportEventThumbnailFrame } from '@/types/report';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { thumbnailFrameUrl } from '@/utils/report-event-thumbnails';
import { ThumbnailItemRow } from './ThumbnailItemRow';

interface ManageThumbnailsListProps {
    sourceId: string;
    thumbnails: ReportEventThumbnailFrame[];
    onThumbnailsChange: (next: ReportEventThumbnailFrame[]) => void;
}

function sortableIdForFrame(frame: ReportEventThumbnailFrame): string {
    return `${frame.time}:${frame.bbox.join(':')}`;
}

export function ManageThumbnailsList({
    sourceId,
    thumbnails,
    onThumbnailsChange,
}: ManageThumbnailsListProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const itemIds = thumbnails.map(sortableIdForFrame);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const fromIndex = itemIds.indexOf(String(active.id));
        const toIndex = itemIds.indexOf(String(over.id));
        if (fromIndex < 0 || toIndex < 0) {
            return;
        }
        onThumbnailsChange(arrayMove(thumbnails, fromIndex, toIndex));
    };

    const handleRemoveAtIndex = (index: number) => {
        onThumbnailsChange(
            thumbnails.filter((_, itemIndex) => itemIndex !== index),
        );
    };

    if (thumbnails.length === 0) {
        return (
            <p className="w-full py-1 text-left text-sm text-grey-400">
                {t('eventEditor.manageThumbnailsEmpty')}
            </p>
        );
    }

    return (
        <DndContext
            autoScroll={false}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={itemIds}
                strategy={verticalListSortingStrategy}
            >
                <div className="flex w-full flex-col items-stretch gap-2 pb-1">
                    {thumbnails.map((frame, index) => {
                        const sortableId = sortableIdForFrame(frame);
                        return (
                            <ThumbnailItemRow
                                key={sortableId}
                                id={sortableId}
                                previewUrl={thumbnailFrameUrl(sourceId, frame)}
                                onRemove={() => handleRemoveAtIndex(index)}
                            />
                        );
                    })}
                </div>
            </SortableContext>
        </DndContext>
    );
}
